import type { ImageSourcePropType } from 'react-native';

import airplaneUrl from '../../assets/images/airplane.webp';
import beachUrl from '../../assets/images/beach.webp';
import churchesUrl from '../../assets/images/churches.webp';
import cruiseUrl from '../../assets/images/cruise.webp';
import fujiUrl from '../../assets/images/fuji.webp';
import germanyUrl from '../../assets/images/germany.webp';
import hollandUrl from '../../assets/images/holland.webp';
import hongkongUrl from '../../assets/images/hongkong.webp';
import koreaUrl from '../../assets/images/korea.webp';
import londonUrl from '../../assets/images/london.webp';
import maldivesUrl from '../../assets/images/maldives.webp';
import mountainUrl from '../../assets/images/mountain.webp';
import parisUrl from '../../assets/images/paris.webp';
import sakuraUrl from '../../assets/images/sakura.webp';
import seaUrl from '../../assets/images/sea.webp';
import shanghaiUrl from '../../assets/images/shanghai.webp';
import singaporeUrl from '../../assets/images/singapore.webp';
import snowUrl from '../../assets/images/snow.webp';
import sunriseUrl from '../../assets/images/sunrise.webp';
import sunsetsUrl from '../../assets/images/sunsets.webp';
import swissUrl from '../../assets/images/swiss.webp';
import thailandUrl from '../../assets/images/thailand.webp';
import vietnamUrl from '../../assets/images/vietnam.webp';
import waterfallsUrl from '../../assets/images/waterfalls.webp';
import brandLogoPngUrl from '../../assets/images/brand-logo.png';
import brandLogoSvgUrl from '../../assets/images/brand-logo.svg';
import brandLogoHdPngUrl from '../../assets/images/brand-logo-hd.png';
import brandLogoHdSvgUrl from '../../assets/images/brand-logo-hd.svg';
import brandLogoSmallPngUrl from '../../assets/images/brand-logo-small.png';
import brandLogoSmallSvgUrl from '../../assets/images/brand-logo-small.svg';
import brandLogoTransparentPngUrl from '../../assets/images/brand-logo-transparent.png';
import brandLogoTransparentSvgUrl from '../../assets/images/brand-logo-transparent.svg';
import logoPngUrl from '../../assets/images/logo.png';
import logoTestSimplePngUrl from '../../assets/images/logo-test-simple.png';
import logoTestSimpleSvgUrl from '../../assets/images/logo-test-simple.svg';

const createImageSource = (uri: string): ImageSourcePropType => ({ uri });

const IMAGE_ASSETS = {
  'assets/images/airplane.webp': createImageSource(airplaneUrl),
  'assets/images/beach.webp': createImageSource(beachUrl),
  'assets/images/churches.webp': createImageSource(churchesUrl),
  'assets/images/cruise.webp': createImageSource(cruiseUrl),
  'assets/images/fuji.webp': createImageSource(fujiUrl),
  'assets/images/germany.webp': createImageSource(germanyUrl),
  'assets/images/holland.webp': createImageSource(hollandUrl),
  'assets/images/hongkong.webp': createImageSource(hongkongUrl),
  'assets/images/korea.webp': createImageSource(koreaUrl),
  'assets/images/london.webp': createImageSource(londonUrl),
  'assets/images/maldives.webp': createImageSource(maldivesUrl),
  'assets/images/mountain.webp': createImageSource(mountainUrl),
  'assets/images/paris.webp': createImageSource(parisUrl),
  'assets/images/sakura.webp': createImageSource(sakuraUrl),
  'assets/images/sea.webp': createImageSource(seaUrl),
  'assets/images/shanghai.webp': createImageSource(shanghaiUrl),
  'assets/images/singapore.webp': createImageSource(singaporeUrl),
  'assets/images/snow.webp': createImageSource(snowUrl),
  'assets/images/sunrise.webp': createImageSource(sunriseUrl),
  'assets/images/sunsets.webp': createImageSource(sunsetsUrl),
  'assets/images/swiss.webp': createImageSource(swissUrl),
  'assets/images/thailand.webp': createImageSource(thailandUrl),
  'assets/images/vietnam.webp': createImageSource(vietnamUrl),
  'assets/images/waterfalls.webp': createImageSource(waterfallsUrl),
  'assets/images/brand-logo.png': createImageSource(brandLogoPngUrl),
  'assets/images/brand-logo.svg': createImageSource(brandLogoSvgUrl),
  'assets/images/brand-logo-hd.png': createImageSource(brandLogoHdPngUrl),
  'assets/images/brand-logo-hd.svg': createImageSource(brandLogoHdSvgUrl),
  'assets/images/brand-logo-small.png': createImageSource(brandLogoSmallPngUrl),
  'assets/images/brand-logo-small.svg': createImageSource(brandLogoSmallSvgUrl),
  'assets/images/brand-logo-transparent.png': createImageSource(brandLogoTransparentPngUrl),
  'assets/images/brand-logo-transparent.svg': createImageSource(brandLogoTransparentSvgUrl),
  'assets/images/logo.png': createImageSource(logoPngUrl),
  'assets/images/logo-test-simple.png': createImageSource(logoTestSimplePngUrl),
  'assets/images/logo-test-simple.svg': createImageSource(logoTestSimpleSvgUrl),
} as const satisfies Record<string, ImageSourcePropType>;

const DEFAULT_TRAVEL_IMAGE_KEY = 'assets/images/sunrise.webp';

export type SharedImageKey = keyof typeof IMAGE_ASSETS;

export const getSharedImageAsset = (key?: string | null): ImageSourcePropType | undefined => {
  if (!key) return undefined;
  const k = String(key);
  if (k.startsWith('http://') || k.startsWith('https://') || k.startsWith('data:')) {
    return createImageSource(k);
  }
  return IMAGE_ASSETS[k as SharedImageKey];
};

// 尝试将多种输入形式转换为 ImageSource：
// - 已有的 ImageSource 直接返回
// - http/https/data URL => { uri }
// - 资源键（assets/images/xxx.webp|png|svg）
// - 文件名（xxx.webp|png|svg）
// - 裸键（如 fuji => assets/images/fuji.webp）
const toImageSourceMaybe = (
  key?: string | null | ImageSourcePropType,
): ImageSourcePropType | undefined => {
  if (!key) return undefined;
  if (typeof key === 'object') return key as ImageSourcePropType;

  const raw = String(key).trim();
  if (!raw) return undefined;

  if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('data:')) {
    return createImageSource(raw);
  }

  // 直接键命中
  const direct = IMAGE_ASSETS[raw as SharedImageKey];
  if (direct) return direct;

  // 去除路径，仅保留文件名
  const filename = raw.replace(/^.*[\\/]/, '');
  const withPrefix = `assets/images/${filename}` as SharedImageKey;
  if (IMAGE_ASSETS[withPrefix]) return IMAGE_ASSETS[withPrefix];

  // 裸键（无扩展名）默认尝试 .webp
  const bare = filename.replace(/\.(webp|png|svg)$/i, '');
  const bareWithWebp = `assets/images/${bare}.webp` as SharedImageKey;
  if (IMAGE_ASSETS[bareWithWebp]) return IMAGE_ASSETS[bareWithWebp];

  return undefined;
};

// 将 key 转换为 ImageSource（带回退）
export const getTravelBackgroundAsset = (
  key?: string | null | ImageSourcePropType,
): ImageSourcePropType => {
  const maybe = toImageSourceMaybe(key);
  return maybe || IMAGE_ASSETS[DEFAULT_TRAVEL_IMAGE_KEY];
};

// 将 key 转换为 ImageSource（不回退）
export const getTravelBackgroundAssetNoFallback = (
  key?: string | null | ImageSourcePropType,
): ImageSourcePropType | undefined => {
  return toImageSourceMaybe(key);
};

export const listAvailableTravelBackgrounds = (): Array<{ key: string; source: ImageSourcePropType }> => {
  const entries = Object.entries(IMAGE_ASSETS);
  return entries
    .filter(([key]) => key.startsWith('assets/images/') && key.endsWith('.webp'))
    .map(([key, source]) => ({ key, source }));
};

export const sharedImageKeys = Object.keys(IMAGE_ASSETS);

/**
 * 统一解析旅行背景图的工具：从不同数据形状中提取 key/URL
 * 支持以下来源优先级：
 * - itinerary.background_image
 * - itinerary.background?.relativePath
 * - itinerary.selectedBackground?.relativePath
 * - form.selectedBackground?.relativePath
 * - itinerary.backgroundUrl / form.backgroundUrl（URL）
 * - itinerary.background / form.background（可能是 key/URL）
 */
export const resolveTravelBackgroundSource = (
  itinerary?: any,
  form?: any,
): ImageSourcePropType => {
  const tryKeys: any[] = [
    // 常见字段（下划线命名）
    itinerary?.background_image_url,
    form?.background_image_url,
    itinerary?.background_url,
    form?.background_url,
    // 常见字段（驼峰命名）
    itinerary?.backgroundImageUrl,
    form?.backgroundImageUrl,
    itinerary?.background_image,
    itinerary?.background?.relativePath,
    itinerary?.selectedBackground?.relativePath,
    form?.selectedBackground?.relativePath,
    itinerary?.backgroundUrl,
    form?.backgroundUrl,
    itinerary?.background,
    form?.background,
  ].filter(Boolean);

  for (const key of tryKeys) {
    const src = getTravelBackgroundAsset(key as any);
    if (src) return src;
  }
  return getTravelBackgroundAsset(undefined);
};

/**
 * 与 resolveTravelBackgroundSource 相同，但在未命中时不回退默认图，返回 undefined。
 * 适用于避免“先渲染默认图后再闪烁为真实图”的场景。
 */
export const resolveTravelBackgroundSourceLoose = (
  itinerary?: any,
  form?: any,
): ImageSourcePropType | undefined => {
  const tryKeys: any[] = [
    itinerary?.background_image_url,
    form?.background_image_url,
    itinerary?.background_url,
    form?.background_url,
    itinerary?.backgroundImageUrl,
    form?.backgroundImageUrl,
    itinerary?.background_image,
    itinerary?.background?.relativePath,
    itinerary?.selectedBackground?.relativePath,
    form?.selectedBackground?.relativePath,
    itinerary?.backgroundUrl,
    form?.backgroundUrl,
    itinerary?.background,
    form?.background,
  ].filter(Boolean);

  for (const key of tryKeys) {
    const src = getTravelBackgroundAssetNoFallback(key as any);
    if (src) return src;
  }
  return undefined;
};
